import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Linkedin,
  Twitter,
  Instagram,
  Mail,
  Github,
  ArrowRight,
  Award,
  Users,
  Code,
  Target,
  Heart,
  Zap,
  Shield,
  Globe,
  Rocket,
  TrendingUp,
  CheckCircle2,
  Briefcase,
  Lightbulb,
  Star,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { cn } from "../../lib/utils";
import samarthImg from "../../assets/img/team/samarth.jpg";
import DirectorImg from "../../assets/img/team/dev.jpg";
import CTOImg from "../../assets/img/team/team-1.jpg";
import projectManagerImg from "../../assets/img/team/Vijay.jpg";
import SeniorMernDevImg from "../../assets/img/team/FahadMasood.jpg";
import shatishImg from "../../assets/img/team/shatish.jpeg";
import girirajImg from "../../assets/img/team/giriraj.jpeg";

const teamMembers = [
  {
    name: "Deepak Yadav",
    role: "Founder & Director",
    department: "Leadership",
    imgSrc: DirectorImg,
    bio: "Visionary leader with expertise in technology strategy and business development.",
    expertise: [ "Strategic Planning", "Business Development", "Leadership" ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/deepak-yadav%E2%9C%94-b32339221/",
      twitter: "https://twitter.com",
      email: "deepak@oethical.com",
    },
  },
  {
    name: "Vishal Manikpuri",
    role: "Chief Technology Officer",
    department: "Technology",
    imgSrc: CTOImg,
    bio: "Tech innovator driving cutting-edge solutions and architectural excellence.",
    expertise: [ "System Architecture", "Cloud Computing", "DevOps" ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      github: "https://github.com",
    },
  },
  {
    name: "Vijay Jawalkar",
    role: "Project Manager",
    department: "Management",
    imgSrc: projectManagerImg,
    bio: "Experienced project manager ensuring seamless delivery and client satisfaction.",
    expertise: [ "Agile Methodology", "Team Leadership", "Client Relations" ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      email: "vijay@oethical.com",
    },
  },
  {
    name: "Fahad Masood",
    role: "Senior MERN Stack Developer",
    department: "Development",
    imgSrc: SeniorMernDevImg,
    bio: "Full-stack expert building scalable and performant web applications.",
    expertise: [ "MERN Stack", "Node.js", "React.js" ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      github: "https://github.com",
      twitter: "https://twitter.com",
    },
  },
  {
    name: "Samarth Malviya",
    role: "MERN Stack Developer",
    department: "Development",
    imgSrc: samarthImg,
    bio: "Passionate developer creating innovative solutions with modern technologies.",
    expertise: [ "React.js", "MongoDB", "Express.js" ],
    socialLinks: {
      linkedin:
        "https://www.linkedin.com/in/shivam-rai-5b4b01208?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
      instagram: "https://www.instagram.com/shivamrai0420/",
      github: "https://github.com",
    },
  },
  {
    name: "Giriraj Khare Singh",
    role: "Chief Operating Officer",
    department: "Leadership",
    imgSrc: girirajImg,
    bio: "Strategic leader overseeing operations and ensuring organizational excellence and efficiency across all departments.",
    expertise: [ "Operations Management", "Strategic Planning", "Business Excellence" ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      email: "giriraj@oethical.com",
    },
  },
  {
    name: "Satish Thakur",
    role: "Head of Digital Marketing",
    department: "Leadership",
    imgSrc: shatishImg,
    bio: "Digital marketing strategist driving brand growth and online presence through innovative campaigns and data-driven insights.",
    expertise: [ "Digital Marketing", "SEO/SEM", "Brand Strategy" ],
    socialLinks: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      email: "satish@oethical.com",
    },
  },
];

const departments = [
  {
    name: "Leadership",
    icon: Target,
    description: "Strategic vision and executive leadership",
    color: "text-blue-500",
    bgGradient: "from-blue-500/10 to-cyan-500/10",
  },
  {
    name: "Technology",
    icon: Code,
    description: "Innovation and technical excellence",
    color: "text-purple-500",
    bgGradient: "from-purple-500/10 to-pink-500/10",
  },
  {
    name: "Development",
    icon: Rocket,
    description: "Building cutting-edge solutions",
    color: "text-green-500",
    bgGradient: "from-green-500/10 to-emerald-500/10",
  },
  {
    name: "Management",
    icon: Briefcase,
    description: "Project delivery and client success",
    color: "text-orange-500",
    bgGradient: "from-orange-500/10 to-amber-500/10",
  },
];

const companyValues = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We constantly push boundaries and embrace new technologies to deliver cutting-edge solutions.",
    color: "text-yellow-500",
  },
  {
    icon: Heart,
    title: "Passion",
    description: "Our team is driven by passion for technology and commitment to excellence in every project.",
    color: "text-red-500",
  },
  {
    icon: Shield,
    title: "Integrity",
    description: "We maintain the highest standards of honesty, transparency, and ethical practices.",
    color: "text-blue-500",
  },
  {
    icon: Users,
    title: "Collaboration",
    description: "We believe in the power of teamwork and collaborative problem-solving to achieve great results.",
    color: "text-green-500",
  },
];

const stats = [
  { icon: Users, value: "50+", label: "Team Members", color: "text-blue-500" },
  { icon: Award, value: "200+", label: "Projects Delivered", color: "text-purple-500" },
  { icon: Globe, value: "15+", label: "Countries Served", color: "text-green-500" },
  { icon: Star, value: "98%", label: "Client Satisfaction", color: "text-orange-500" },
];

const Team = () => {
  const [ selectedDepartment, setSelectedDepartment ] = useState( "All" );
  const [ hoveredMember, setHoveredMember ] = useState( null );

  useEffect( () => {
    AOS.init( {
      duration: 1000,
      once: true,
      offset: 100,
    } );
  }, [] );

  const filteredMembers =
    selectedDepartment === "All"
      ? teamMembers
      : teamMembers.filter( ( member ) => member.department === selectedDepartment );

  const departmentsList = [ "All", ...new Set( teamMembers.map( ( m ) => m.department ) ) ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */ }
      <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-background via-primary/5 to-background border-b overflow-hidden">
        {/* Background Pattern */ }
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div
            className="absolute inset-0"
            style={ {
              backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
              backgroundSize: "50px 50px",
            } }
          />
        </div>

        {/* Animated Gradient Orbs */ }
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse delay-1000" />

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <div className="inline-block" data-aos="fade-down" data-aos-delay="100">
              <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                Our Team
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Meet Our{ " " }
              <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Exceptional Team
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              We take pride in our skilled professionals who are committed to delivering
              exceptional results and driving innovation in the IT industry. Our diverse team
              brings together expertise, creativity, and passion to solve complex challenges.
            </p>

            {/* Stats */ }
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              { stats.map( ( stat, index ) => {
                const Icon = stat.icon;
                return (
                  <div key={ index } className="text-center">
                    <div className="flex justify-center mb-2">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className={ cn( "h-6 w-6", stat.color ) } />
                      </div>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                      { stat.value }
                    </div>
                    <div className="text-sm text-muted-foreground">{ stat.label }</div>
                  </div>
                );
              } ) }
            </div>
          </div>
        </div>
      </section>

      {/* Departments Section */ }
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-primary">Departments</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground">
              Explore the diverse expertise across our organization
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            { departments.map( ( dept, index ) => {
              const Icon = dept.icon;
              return (
                <Card
                  key={ index }
                  data-aos="fade-up"
                  data-aos-delay={ 100 + index * 100 }
                  className={ cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl text-center cursor-pointer",
                    "bg-gradient-to-br",
                    dept.bgGradient,
                    selectedDepartment === dept.name && "border-primary border-2"
                  ) }
                  onClick={ () => setSelectedDepartment( dept.name ) }
                >
                  <CardHeader>
                    <div className="h-16 w-16 rounded-xl bg-background/80 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                      <Icon className={ cn( "h-8 w-8", dept.color ) } />
                    </div>
                    <CardTitle className="text-xl mb-2">{ dept.name }</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{ dept.description }</p>
                  </CardContent>
                </Card>
              );
            } ) }
          </div>

          {/* Department Filter Tabs */ }
          <div className="flex justify-center mb-8">
            <Tabs
              value={ selectedDepartment }
              onValueChange={ setSelectedDepartment }
              className="w-full max-w-2xl"
            >
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
                { departmentsList.map( ( dept ) => (
                  <TabsTrigger key={ dept } value={ dept } className="text-sm">
                    { dept }
                  </TabsTrigger>
                ) ) }
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Team Members Section */ }
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            { filteredMembers.map( ( member, index ) => (
              <Card
                key={ index }
                data-aos="fade-up"
                data-aos-delay={ 100 + index * 100 }
                className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group overflow-hidden"
                onMouseEnter={ () => setHoveredMember( index ) }
                onMouseLeave={ () => setHoveredMember( null ) }
              >
                <div className="relative overflow-hidden">
                  <div className="aspect-[4/5] relative overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
                    <img
                      src={ member.imgSrc }
                      alt={ member.name }
                      className={ cn(
                        "w-full h-full object-cover transition-transform duration-500",
                        hoveredMember === index && "scale-110"
                      ) }
                    />
                    <div
                      className={ cn(
                        "absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent transition-opacity duration-300",
                        hoveredMember === index ? "opacity-100" : "opacity-0"
                      ) }
                    />
                    <div
                      className={ cn(
                        "absolute bottom-0 left-0 right-0 p-6 transition-all duration-300",
                        hoveredMember === index
                          ? "translate-y-0 opacity-100"
                          : "translate-y-4 opacity-0"
                      ) }
                    >
                      <p className="text-sm text-foreground mb-3 line-clamp-2">
                        { member.bio }
                      </p>
                      <div className="flex flex-wrap gap-2">
                        { member.expertise.slice( 0, 2 ).map( ( skill, idx ) => (
                          <span
                            key={ idx }
                            className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full"
                          >
                            { skill }
                          </span>
                        ) ) }
                      </div>
                    </div>
                  </div>
                </div>

                <CardHeader className="text-center">
                  <CardTitle className="text-xl mb-1">{ member.name }</CardTitle>
                  <CardDescription className="text-base font-medium text-primary">
                    { member.role }
                  </CardDescription>
                  <div className="text-sm text-muted-foreground mt-1">
                    { member.department }
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="flex justify-center gap-3">
                    { member.socialLinks.linkedin && (
                      <a
                        href={ member.socialLinks.linkedin }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 group/link"
                      >
                        <Linkedin className="h-5 w-5 group-hover/link:scale-110 transition-transform" />
                      </a>
                    ) }
                    { member.socialLinks.github && (
                      <a
                        href={ member.socialLinks.github }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 group/link"
                      >
                        <Github className="h-5 w-5 group-hover/link:scale-110 transition-transform" />
                      </a>
                    ) }
                    { member.socialLinks.twitter && (
                      <a
                        href={ member.socialLinks.twitter }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 group/link"
                      >
                        <Twitter className="h-5 w-5 group-hover/link:scale-110 transition-transform" />
                      </a>
                    ) }
                    { member.socialLinks.instagram && (
                      <a
                        href={ member.socialLinks.instagram }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 group/link"
                      >
                        <Instagram className="h-5 w-5 group-hover/link:scale-110 transition-transform" />
                      </a>
                    ) }
                    { member.socialLinks.email && (
                      <a
                        href={ `mailto:${ member.socialLinks.email }` }
                        className="h-10 w-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 group/link"
                      >
                        <Mail className="h-5 w-5 group-hover/link:scale-110 transition-transform" />
                      </a>
                    ) }
                  </div>
                </CardContent>
              </Card>
            ) ) }
          </div>
        </div>
      </section>

      {/* Company Values Section */ }
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div
            className="text-center max-w-3xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-primary">Core Values</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4 mb-6" />
            <p className="text-lg text-muted-foreground">
              The principles that guide our team and shape our culture
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            { companyValues.map( ( value, index ) => {
              const Icon = value.icon;
              return (
                <Card
                  key={ index }
                  data-aos="fade-up"
                  data-aos-delay={ 100 + index * 100 }
                  className="border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl text-center group"
                >
                  <CardHeader>
                    <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Icon className={ cn( "h-8 w-8", value.color ) } />
                    </div>
                    <CardTitle className="text-xl mb-2">{ value.title }</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      { value.description }
                    </p>
                  </CardContent>
                </Card>
              );
            } ) }
          </div>
        </div>
      </section>

      {/* Why Join Us Section */ }
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Join <span className="text-primary">Our Team?</span>
              </h2>
              <div className="w-16 h-1 bg-primary rounded-full mb-6" />
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                We're always looking for talented individuals who share our passion for
                technology and innovation. Join us in building the future of IT solutions.
              </p>

              <div className="space-y-4">
                { [
                  "Competitive compensation and benefits",
                  "Flexible work arrangements",
                  "Continuous learning and development",
                  "Cutting-edge technology stack",
                  "Collaborative and inclusive culture",
                  "Opportunities for career growth",
                ].map( ( benefit, index ) => (
                  <div
                    key={ index }
                    className="flex items-start gap-3"
                    data-aos="fade-up"
                    data-aos-delay={ 200 + index * 50 }
                  >
                    <CheckCircle2 className="h-6 w-6 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{ benefit }</span>
                  </div>
                ) ) }
              </div>

              <div className="mt-8">
                <Button size="lg" className="group">
                  View Open Positions
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>

            <div className="relative" data-aos="fade-left" data-aos-delay="200">
              <div className="grid grid-cols-2 gap-4">
                { [
                  { icon: Zap, label: "Fast Growth", color: "text-yellow-500" },
                  { icon: TrendingUp, label: "Career Growth", color: "text-green-500" },
                  { icon: Shield, label: "Job Security", color: "text-blue-500" },
                  { icon: Heart, label: "Work-Life Balance", color: "text-red-500" },
                ].map( ( item, index ) => {
                  const Icon = item.icon;
                  return (
                    <Card
                      key={ index }
                      className="border-border text-center p-6 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Icon className={ cn( "h-6 w-6", item.color ) } />
                      </div>
                      <p className="font-medium">{ item.label }</p>
                    </Card>
                  );
                } ) }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */ }
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary/10 via-primary/5 to-background border-t">
        <div className="container">
          <Card
            className="border-border shadow-2xl overflow-hidden"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-12 md:p-16">
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                  Ready to Join Our Team?
                </h2>
                <p className="text-lg text-muted-foreground">
                  We're always looking for passionate individuals who want to make a difference
                  in the IT industry. Explore our open positions and become part of our success
                  story.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Button size="lg" className="group" asChild>
                    <Link to="/career">
                      View Careers
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/contactUs">
                      Contact HR
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Team;
